import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FromAndTo } from '@com-delta-omnichannel/mach-core-js';

import {
  DestinationType,
  FROM
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { WidgetEventService } from '../../shared/utils/widget-event.service';
import { VacationFormErrors } from '../models/vacation-form-data.interface';
import {
  DropdownValue,
  VacationWidget,
  VacationWidgetFormType,
  WidgetComponents
} from '../models/vacation-widget.interfaces';
import { DateManagementService } from './date-management.service';
import { FormValidationService } from './form-validation.service';
import { VacationWidgetInitializationService } from './vacation-widget-initialization.service';
@Injectable({
  providedIn: 'root'
})
export class ComponentLifecycleService {
  initService = inject(VacationWidgetInitializationService);
  dateService = inject(DateManagementService);
  validationService = inject(FormValidationService);
  eventService = inject(WidgetEventService);

  initializeWidget(
    content: VacationWidget,
    form: FormGroup<VacationWidgetFormType>,
    isSubmitted: () => boolean,
    onDatePickerClose?: () => void
  ): WidgetComponents {
    const components = this.initService.initializeComponents(content);
    this.setupDateListeners(components, content, onDatePickerClose);
    this.setupDropdownListeners(components, form, isSubmitted, content);
    return components;
  }

  private setupDateListeners(
    components: WidgetComponents,
    content: VacationWidget,
    onDatePickerClose?: () => void
  ): void {
    this.dateService.setupDateChangeListener(
      components.rangeDatePickerInstance,
      content,
      onDatePickerClose
    );
  }

  private setupDropdownListeners(
    components: WidgetComponents,
    form: FormGroup<VacationWidgetFormType>,
    isSubmitted: () => boolean,
    content: VacationWidget
  ): void {
    components.dropdownRoomOptions.onChange((value: DropdownValue) => {
      form.patchValue({ numberOfRooms: +value.value });
      this.eventService.emitErrorCountChange();
    });

    components.dropdownPassengerOptions.onChange((value: DropdownValue) => {
      form.patchValue({ numberOfPassengers: +value.value });
      this.eventService.emitErrorCountChange();
      if (isSubmitted()) {
        this.eventService.emitPassengerError(
          this.validationService.setPassengerError(form, content)
        );
      }
    });

    components.dropdownPassengerOptions.setValue('1');
    components.dropdownRoomOptions.setValue('1');
  }

  updateFormAirport(
    airportCode: string,
    destinationType: DestinationType,
    form: FormGroup<VacationWidgetFormType>,
    airportPicker: FromAndTo,
    content: VacationWidget
  ): string {
    if (!form) return '';
    form.patchValue(
      destinationType === FROM
        ? { originAirportCode: airportCode }
        : { destinationAirportCode: airportCode }
    );
    const error = this.validationService.setAirportError(
      form,
      airportPicker,
      content,
      false
    );
    this.eventService.emitAirportUpdate(airportCode, destinationType);
    this.eventService.emitErrorCountChange();
    return error;
  }

  processFormSubmission(
    form: FormGroup<VacationWidgetFormType>,
    airportPicker: FromAndTo,
    content: VacationWidget
  ): VacationFormErrors {
    const { destination, origin } = airportPicker.getAirportSelections();
    if (destination.code)
      form.patchValue({ destinationAirportCode: destination.code });
    if (origin.code) form.patchValue({ originAirportCode: origin.code });

    return {
      airportError: this.validationService.setAirportError(
        form,
        airportPicker,
        content,
        true
      ),
      dateError: this.validationService.setDateError(form, content),
      passengerError: this.validationService.setPassengerError(form, content),
      maxDatesError: this.validationService.setMaxDateError(form, content)
    };
  }
}
